import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  TextField,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from '@material-ui/core'
import BaseDialog from '../../../BaseDialog'

import { useDataApi } from '../../../../context/DataApiContext'
import { useNotificationsProvider } from '../../../../context/NotificationsContext'
import validators from '../../../../utils/validators'
import AutoCompleteInput from '../../../AutoCompleteInput'

export default function CreateVehicleDialog({ open, onClose, onCreated }) {
  const { t } = useTranslation()

  const { dataProvider } = useDataApi()
  const { showNotification } = useNotificationsProvider()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [vehicleType, setVehicleType] = useState(null)
  const [nSerie, setNSerie] = useState('')
  const [nIntern, setNIntern] = useState('')
  const [plate, setPlate] = useState('')
  const [vehicleState, setVehicleState] = useState('0')
  const [client, setClient] = useState('')

  const resetState = () => {
    // Clean form
    setLoading(false)
    setError('')
    setVehicleType(null)
    setPlate('')
    setNSerie('')
    setNIntern('')
    setVehicleState('0')
    setClient('')
  }

  const onCloseDialog = (event, reason) => {
    if ((reason === 'backdropClick') || (reason === 'escapeKeyDown')) {
      onClose()
    } else {
      resetState()
      onClose()
    }
  }

  const onConfirm = () => {
    setError(null)

    if (vehicleType === null) {
      setError('vehicleType_required')
      return
    }

    if (plate && plate.length > 0 && !validators.isValidPlate(plate)) {
      setError('plate_invalid')
      return
    }

    if (nIntern.length === 0) {
      setError('nIntern_required')
      return
    }

    if (vehicleState === null) {
      setError('vehicleState_required')
      return
    }

    if (client === null && vehicleState === '1') {
      setError('client_required')
      return
    }

    const sanitizedNIntern = nIntern.trim().length === 0 ? undefined : nIntern.trim()
    const data = {
      vehicleTypeId: vehicleType.id,
      plate: plate || null,
      nSerie: nSerie || null,
      nIntern: sanitizedNIntern,
      clientId: vehicleState === '1' && client ? client.id : undefined,
    }

    setLoading(true)
    dataProvider.create('vehicle', { data }).then(() => {
      dataProvider.cache.reset()
      setLoading(false)
      showNotification('success', t('create-vehicle-dialog.success'))
      onCreated()
      resetState()
    }).catch((err) => {
      setLoading(false)
      if (err.response.data.validationErrors && err.response.data.validationErrors.length > 0) {
        showNotification('error', err.response.data.validationErrors[0].errorMessage)
        setError(`${err.response.data.validationErrors[0].field}_invalid`)
      } else {
        showNotification('error', t('errors.api-error'))
      }
    })
  }

  return (
    <BaseDialog
      open={open}
      onClose={onCloseDialog}
      onConfirm={onConfirm}
      title={t('create-vehicle-dialog.title')}
      loading={loading}
    >
      <Grid container direction="column" spacing={4}>
        <Grid item>
          <AutoCompleteInput
            error={error === 'vehicleType_required'}
            onChange={setVehicleType}
            label={t('common.vehicleType')}
            resource="vehicle-type/autocomplete"
            value={vehicleType}
            required
            canDelete
          />
        </Grid>
        <Grid item>
          <TextField
            error={error === 'nSerie_invalid'}
            helperText={error === 'nSerie_invalid'
              ? t('errors.not-valid')
              : t('modify-vehicle-dialog.nSerie-notice')}
            id="nSerie"
            label={t('common.nSerie')}
            fullWidth
            value={nSerie}
            onChange={(event) => setNSerie(event.target.value)}
          />
        </Grid>
        <Grid item>
          <TextField
            required
            error={error === 'nIntern_required' || error === 'nIntern_invalid'}
            helperText={error === 'nIntern_invalid'
              ? t('errors.not-valid')
              : t('common.required')}
            id="nIntern"
            label={t('common.nIntern')}
            fullWidth
            value={nIntern}
            onChange={(event) => setNIntern(event.target.value)}
          />
        </Grid>
        <Grid item>
          <TextField
            error={error === 'plate_invalid'}
            helperText={error === 'plate_invalid'
              ? t('errors.not-valid')
              : t('modify-vehicle-dialog.plate-notice')}
            id="plate"
            label={t('common.plate')}
            fullWidth
            value={plate}
            onChange={(event) => setPlate(event.target.value)}
          />
        </Grid>
        <Grid item>
          <FormControl fullWidth>
            <InputLabel id="vehicleStateSelectLabel">{t('common.vehicleState')}</InputLabel>
            <Select
              error={error === 'vehicleState_required'}
              onChange={(event) => {
                setVehicleState(event.target.value)
                setClient(null)
              }}
              label={t('common.vehicleState')}
              value={vehicleState}
              required
              canDelete
              fullWidth
            >
              <MenuItem value="0">{t('common.vehicleNew')}</MenuItem>
              <MenuItem value="1">{t('common.vehicleSold')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        {
          vehicleState === '1'
          && (
            <Grid item>
              <AutoCompleteInput
                error={error === 'client_required'}
                onChange={setClient}
                label={t('common.client')}
                resource="clients/autocomplete"
                value={client}
                required
                canDelete
              />
            </Grid>
          )
        }
      </Grid>
    </BaseDialog>
  )
}
